import CacheEnum from '@/enums/cacheEnum';
import { useLocalStorage, useSessionStorage } from '@/hooks/useStorage';
import { exchangeConfigApi } from '@/services/api/requestApi';
import { useReq } from '@/services/net/request';
import ProjectSetting from '@/setting/projectSetting';
import RenderUtil from '@/utils/RenderUtil';

// 首先，我所有的钱是印度卢比
// 用印度卢比/exchangeToUSD得到USD金额
// 美元×汇率＝展示价格

interface exchangeConversionProps {
  val: number | string;
  currency?: string;
  isJoin?: boolean;
  isBackend?: boolean;
  joinMode?: 'symbol' | 'coinName';
  showMask?: boolean;
  isPadZeroes?: boolean;
  len?: number;
  defaultExchangeRate?: number;
}

export default () => {
  //设置页选择汇率 1
  const [currentExchangeSetting, setCurrentExchangeSetting] =
    useLocalStorage<any>({
      key: CacheEnum.APP_CURRENT_EXCHANGE_RATE_SETTING, // 全部
      defaultValue: '',
    });

  //资金页选择汇率 2
  const [currentExchangeFunds, setCurrentExchangeFunds] = useLocalStorage<any>({
    key: CacheEnum.APP_CURRENT_EXCHANGE_RATE_FUNDS, // 全部
    defaultValue: '',
  });

  const [isShow, setIsShow] = useSessionStorage({
    key: CacheEnum.APP_SHOW_AMOUNT,
    defaultValue: true,
  });

  // 汇率列表
  const [exchange, setExchangeList] = useLocalStorage<any>({
    key: CacheEnum.APP_EXCHANGE_RATE,
    defaultValue: '',
  });
  //缓存
  const { runAsync: getExchangeData } = useReq(exchangeConfigApi, {
    onSuccess(data: any) {
      if (!currentExchangeSetting && data?.usdExchangeList?.length !== 0) {
        setCurrentExchangeSetting(data?.usdExchangeList[0]);
      } else {
        setCurrentExchangeSetting(
          data?.usdExchangeList?.find(
            (item: any) => item.exchange === currentExchangeSetting?.exchange,
          ),
        );
      }
      if (!currentExchangeFunds && data?.usdExchangeList?.length !== 0) {
        setCurrentExchangeFunds(data?.usdExchangeList[0]);
      } else {
        setCurrentExchangeFunds(
          data?.usdExchangeList?.find(
            (item: any) => item.exchange === currentExchangeFunds?.exchange,
          ),
        );
      }
      setExchangeList(data);
    },
  });
  // 根据币种名称获取汇率
  const getExchange = (currency = ProjectSetting.APP_DEFAULT_CURRENCY) => {
    if (!currency || !currency?.trim) return false;
    let cur = [currency.trim()];
    if (currency === 'USD') {
      cur = ['USDT', ...cur];
    }
    if (currency === 'USDT') {
      cur = ['USD', ...cur];
    }
    if (cur && exchange && exchange?.usdExchangeList) {
      const findCurrency = exchange?.usdExchangeList?.find((item: any) =>
        cur.includes(item?.exchange?.trim()?.toUpperCase()),
      );
      if (findCurrency) return findCurrency;
    }
    return false;
  };
  // 汇率转换
  const exchangeConversionFunds = (
    val: number,
    currency = ProjectSetting.APP_DEFAULT_CURRENCY,
    _defaultExchangeRate?: number,
  ) => {
    // 本项目不进行汇率转换
    let defaultExchangeRate = 1;
    if (defaultExchangeRate) {
      return val * defaultExchangeRate;
    }
    let cur = getExchange(currency);
    if (currency && exchange && exchange?.usdExchangeList) {
      // 查找接口获取的汇率
      const findExchange = exchange?.usdExchangeList?.find(
        (item: any) => item?.exchange === cur?.exchange,
      );
      return val * findExchange?.price;
    }
    return 0;
  };
  // 拼接符号
  const joinSymbol = (price: any, currency?: string, type = ProjectSetting.isMultiCurrencyAccount ? 'coinName': 'symbol') => {
    // 根据符号
    let cur = getExchange(currency);

    if (!cur && type !== 'coinName') return price;
    if (!cur && type === 'coinName') return price + ' ' + currency;
    // 符号拼接
    if (type === 'symbol') {
      // return cur?.position === 1 ? price + cur?.sign : cur?.sign + price;
      return price + ProjectSetting.APP_DEFAULT_CURRENCY;
    }
    // 币种名称拼接
    if (type === 'coinName') {
      if (currency?.trim() === 'USDT') {
        return price + ' USDT';
      }
      if (currency?.trim() === 'USD') {
        return price + ' USD';
      }
      return price + ' '+cur?.exchange;
    }
    return price;
  };
  // 获取当前货币长度，或者指定长度
  const getMoneyLength = (target = ProjectSetting.APP_DEFAULT_CURRENCY) => {
    let targetCN = target?.trim?.();
    if (targetCN === 'USD') {
      targetCN = 'USDT';
    }
    // 如果指定了就返回
    const current = exchange?.usdExchangeList?.find(
      (item: any) => item?.exchange === targetCN,
    );
    if (target) {
      if ([undefined, null].includes(current?.priceAccuracy)) return undefined;
      return current?.priceAccuracy;
    }
    // 如果没有就返回正常
    if ([undefined, null].includes(currentExchangeFunds?.priceAccuracy))
      return undefined;
    if (+currentExchangeFunds?.priceAccuracy <= 8) {
      return currentExchangeFunds?.priceAccuracy;
    }
    return 8;
  };

  // 跟随系统显示隐藏
  function maskPrice(price: string | any, showPrice: boolean): string {
    if (!showPrice) {
      return '*'.repeat(6);
    }

    // 判断price组成元素是否只有0和小数点
    if (
      price
        ?.toString()
        ?.split('')
        .every((item: any) => item === '0' || item === ',') ||
      !price
    ) {
      return '--';
    }

    return price;
  }

  // 数据转换综合体方法，不再单独调用其他方法统一管理
  const exchangeConversion = ({
    // 需要转换的值
    val = 0,
    // 符号名
    currency = ProjectSetting.APP_DEFAULT_CURRENCY,
    // 是否根据后端保留小数
    isBackend = true,
    // 是否补0
    isPadZeroes = false,
    // 指定长度，默认两位
    len = 2,
    // 是否拼接符号
    isJoin = true,
    // 拼接符号方式
    joinMode = 'symbol',
    // 是否根据系统显示隐藏
    showMask = false,
    // 指定汇率
    defaultExchangeRate,
  }: exchangeConversionProps) => {

    let cur = getExchange(currency);
    // 返回的最终值
    let result: number | string = val;

    // 汇率转换后的值--根据后端设定的小数位数控制显示
    if (cur) {
      result = exchangeConversionFunds(
        Number(result),
        cur?.exchange,
        defaultExchangeRate,
      );
    }

    result = RenderUtil.FormatAmount(result, getMoneyLength(cur?.exchange));

    // 保留小数
    // if (isBackend) {
    //   result = RenderUtil.FormatAmount(result, getMoneyLength(cur?.exchange));
    // }
    // 是否部位
    if (isPadZeroes) {
      result = RenderUtil.FormatAmount(result, len, isPadZeroes);
    }
    // 跟随系统显示隐藏
    if (showMask) {
      result = maskPrice(result, isShow);
    }
    // 拼接符号
    if (isJoin) {
      result = joinSymbol(result, currency, joinMode);
    }
    return result;
  };

  //查找选定的币种给出多少USD计算出对应币种的金额
  const findExchangeToMoney = (val: number, type: any, isMax = true) => {
    const findCurrency = exchange?.usdExchangeList?.find(
      (item: any) => item.exchange === type,
    );
    if (!findCurrency) return val;
    if (isMax) {
      return findCurrency?.price * val - findCurrency?.price;
    } else {
      return findCurrency?.price * val + findCurrency?.price;
    }
  };

  // 获取指定的币的单位
  const getExchangeUnit = (currency: string) => {
    let target = currency;
    if (target === 'USD') {
      target = 'USDT';
    }

    

    target = target?.trim?.();
    const findCurrency = exchange?.usdExchangeList?.find(
      (item: any) => item.exchange === target,
    );
    if (!findCurrency) return currency;
    return findCurrency?.sign;
  };

  return {
    exchange, // 后端返回的数据
    setCurrentExchangeFunds, //资金页面选择
    setCurrentExchangeSetting, //汇率设置页面页面选择
    exchangeConversionFunds,
    currentExchangeSetting,
    currentExchangeFunds,
    joinSymbol,
    findExchangeToMoney,
    getMoneyLength,
    setExchangeList,
    getExchangeUnit,
    exchangeConversion,
    isShow,
    setIsShow,
    getExchange,
    getExchangeData,
    maskPrice,
  };
};
